import apiClient from '../api/client';
import { ApiResponse } from '../../types/api.types';

export interface MasterProduct {
  id: number;
  sku: string;
  product_name: string;
  brand: string | null;
  country: string | null;
  country_priority: string | null;
  category: string;
  subcategory: string | null;
  package_size: string | null;
  temperature_type: 'shelf_stable' | 'refrigerated' | 'frozen' | 'cold_chain';
  tax_category: 'exempt_grocery' | 'standard_taxable' | 'reduced';
  suggested_retail_price: string | number;
  wholesale_target_price: string | number;
  cost_price: string | number;
  estimated_gross_margin: string | number;
  description: string | null;
  ingredients?: string | null;
  allergens?: string | null;
  storage_instructions?: string | null;
  research_status?: string | null;
  catalog_status?: string | null;
  production_availability?: string | null;
  production_readiness?: string | null;
  production_status?: string | null;
  recommended_rollout?: string | null;
  is_active: number | boolean;
  created_at: string;
  updated_at: string;
  images?: string[];
  assets?: ProductAsset[];
}

export interface ProductAsset {
  id: number;
  master_product_id: number;
  image_path: string;
  angle_type: 'primary' | 'angle_2' | 'angle_3' | 'angle_4' | 'nutrition' | 'lifestyle';
  is_primary: number;
  display_order: number;
}

export interface TaxRule {
  id: number;
  state_code: string;
  state_name: string;
  standard_sales_tax_rate: string | number;
  grocery_tax_exempt: number;
  prepared_food_tax_rate: string | number;
  is_active: number;
}

export interface CatalogStats {
  totalProducts: number;
  countryBreakdown: { country: string; count: number; avg_price: number }[];
  categoryBreakdown: { category: string; count: number }[];
  taxStats: { tax_category: string; count: number }[];
}

export interface ProductQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  country?: string;
  category?: string;
  tax_category?: string;
  temperature_type?: string;
  is_active?: string | number;
}

class MasterCatalogService {
  async getProducts(params: ProductQueryParams = {}): Promise<{ products: MasterProduct[]; pagination: any }> {
    const response = await apiClient.get<ApiResponse<{ products: MasterProduct[]; pagination: any }>>(
      '/admin/master-catalog/products',
      { params }
    );
    return response.data.data;
  }

  async getProductById(id: number | string): Promise<MasterProduct> {
    const response = await apiClient.get<ApiResponse<MasterProduct>>(`/admin/master-catalog/products/${id}`);
    return response.data.data;
  }

  async createProduct(data: Partial<MasterProduct>): Promise<any> {
    const response = await apiClient.post<ApiResponse<any>>('/admin/master-catalog/products', data);
    return response.data;
  }

  async updateProduct(id: number | string, data: Partial<MasterProduct>): Promise<any> {
    const response = await apiClient.put<ApiResponse<any>>(`/admin/master-catalog/products/${id}`, data);
    return response.data;
  }

  async deleteProduct(id: number | string): Promise<any> {
    const response = await apiClient.delete<ApiResponse<any>>(`/admin/master-catalog/products/${id}`);
    return response.data;
  }

  async addAsset(
    productId: number | string,
    assetData: { image_path?: string; angle_type?: string; is_primary?: number; file?: File } | FormData
  ): Promise<any> {
    let payload: any = assetData;
    let headers: Record<string, string> = {};

    if (!(assetData instanceof FormData) && assetData.file) {
      const formData = new FormData();
      formData.append('file', assetData.file);
      if (assetData.angle_type) formData.append('angle_type', assetData.angle_type);
      if (assetData.is_primary !== undefined) formData.append('is_primary', String(assetData.is_primary));
      if (assetData.image_path) formData.append('image_path', assetData.image_path);
      payload = formData;
      headers['Content-Type'] = 'multipart/form-data';
    } else if (assetData instanceof FormData) {
      headers['Content-Type'] = 'multipart/form-data';
    }

    const response = await apiClient.post<ApiResponse<any>>(
      `/admin/master-catalog/products/${productId}/assets`,
      payload,
      { headers }
    );
    return response.data;
  }

  // Alias for backward compatibility
  async addProductAsset(
    productId: number | string,
    assetData: { image_path?: string; angle_type?: string; is_primary?: number; file?: File } | FormData
  ): Promise<any> {
    return this.addAsset(productId, assetData);
  }

  async deleteAsset(assetId: number | string): Promise<any> {
    const response = await apiClient.delete<ApiResponse<any>>(`/admin/master-catalog/assets/${assetId}`);
    return response.data;
  }

  async pullToStore(data: { businessId: number; masterProductId: number; customPrice?: number; initialStock?: number }): Promise<any> {
    const response = await apiClient.post<ApiResponse<any>>('/admin/master-catalog/pull-to-store', data);
    return response.data;
  }

  async getCatalogStats(): Promise<CatalogStats> {
    const response = await apiClient.get<ApiResponse<CatalogStats>>('/admin/master-catalog/stats');
    return response.data.data;
  }

  async getTaxRules(): Promise<TaxRule[]> {
    const response = await apiClient.get<ApiResponse<TaxRule[]>>('/admin/tax-rules');
    return response.data.data;
  }

  async updateTaxRule(id: number | string, data: Partial<TaxRule>): Promise<any> {
    const response = await apiClient.put<ApiResponse<any>>(`/admin/tax-rules/${id}`, data);
    return response.data;
  }
}

export default new MasterCatalogService();
